import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

// Imagem de um produto da loja, com a regra de precedência:
//   1. imagem_url  → foto que o organizador subiu
//   2. imagem_slug → ilustração no bucket `capas/produtos/{slug}`
//   3. nenhuma das duas → emoji da categoria, num fundo neutro
// Se uma imagem falhar, cai para a próxima da lista — nunca mostra
// ícone de imagem quebrada.

interface ImagemProdutoProps {
  imagemUrl: string | null;
  imagemSlug: string | null;
  emoji: string | null;
  nome: string;
  tamanho?: 'miniatura' | 'grande' | 'media';
  className?: string;
  lazy?: boolean;
}

export default function ImagemProduto({
  imagemUrl,
  imagemSlug,
  emoji,
  nome,
  tamanho = 'miniatura',
  className = '',
  lazy = false,
}: ImagemProdutoProps) {
  const candidatos = useMemo(() => {
    const lista: string[] = [];
    if (imagemUrl) lista.push(imagemUrl);
    if (imagemSlug) {
      lista.push(
        supabase.storage.from('capas').getPublicUrl(`produtos/${imagemSlug}`).data.publicUrl,
      );
    }
    return lista;
  }, [imagemUrl, imagemSlug]);

  const [indice, setIndice] = useState(0);

  // Se o produto trocar de imagem (ex.: escolheu outro desenho), volta ao topo.
  useEffect(() => {
    setIndice(0);
  }, [imagemUrl, imagemSlug]);

  const src = indice < candidatos.length ? candidatos[indice] : null;

  const base =
    tamanho === 'grande'
      ? 'aspect-square w-full'
      : tamanho === 'media'
        ? 'h-[60px] w-[60px] shrink-0'
        : 'h-10 w-10 shrink-0';
  const emojiCls = tamanho === 'grande' ? 'text-5xl' : tamanho === 'media' ? 'text-2xl' : 'text-xl';

  if (!src) {
    return (
      <span
        aria-hidden="true"
        className={`flex items-center justify-center rounded-lg bg-secondary-100 ${base} ${className}`}
      >
        <span className={emojiCls}>{emoji || '🛍️'}</span>
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={nome}
      loading={lazy ? 'lazy' : undefined}
      onError={() => setIndice((i) => i + 1)}
      className={`rounded-lg object-cover ${base} ${className}`}
    />
  );
}